import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Star, Clock, Shield, ChevronDown, Sparkles, Sprout, Sun, Smile, Brain, Activity, Eye, Ear, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { CATEGORIES } from '../data/categories';
import { ARTICLES } from '../data/articles';
import { trackCategory, trackClinic } from '../lib/tracking';
import LeadForm from '../components/LeadForm';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import { treatmentBySlug } from '../data/treatments';

const ICONS = { sparkles: Sparkles, sprout: Sprout, sun: Sun, smile: Smile, brain: Brain, activity: Activity, eye: Eye, ear: Ear };

// Representative hero image per category (uses generated PNGs)
const CATEGORY_IMAGES = {
  'estetikk-og-kosmetikk': '/images/treatments/botox.png',
  'har-og-hodebunn': '/images/treatments/hartransplantasjon.png',
  'hud-og-laser': '/images/treatments/laserbehandling.png',
  'tannhelse-og-smil': '/images/treatments/tannbleking.png',
  'fysioterapi-og-kiropraktor': '/images/treatments/fysioterapi.png',
  'oye-og-syn': '/images/treatments/oyenlaser.png',
  'ore-nese-hals': '/images/treatments/onh-spesialist.png',
};

const FAQ = [
  { q: 'Er tjenesten gratis for pasienter?', a: 'Ja, Specialists.no er helt gratis og uforpliktende for deg som pasient. Du kan når som helst velge å ikke gå videre med en klinikk.' },
  { q: 'Hvem ser opplysningene mine?', a: 'Kun klinikken du velger får dine kontaktopplysninger. Vi deler aldri informasjon med tredjeparter utover den valgte klinikken.' },
  { q: 'Hva skjer etter jeg sender inn skjemaet?', a: 'Vi sender forespørselen direkte til klinikken du har valgt. De kontakter deg innen 24 timer i åpningstid via telefon eller e-post.' },
  { q: 'Kan jeg velge hvilken klinikk som kontakter meg?', a: 'Ja, i skjemaet kan du velge din foretrukne klinikk basert på by og behandling. Du kan også la oss foreslå en egnet klinikk.' },
  { q: 'Hva gjør dere hvis jeg ikke hører noe innen 24 timer?', a: 'Kontakt oss på kontakt@specialists.no så følger vi opp med klinikken. Våre partnere forplikter seg til rask responstid.' },
  { q: 'Kan jeg trekke samtykket mitt?', a: 'Ja, kontakt oss eller klinikken direkte. Vi sletter dine data på forespørsel, og senest etter 12 måneder automatisk.' },
  { q: 'Hvilke behandlinger dekker dere?', a: 'Vi dekker estetiske behandlinger, tannhelse, hud- og laserbehandling, fysioterapi og kiropraktor, øye- og syn, samt øre-nese-hals.' },
  { q: 'Hva betyr 4+ stjerner kravet?', a: 'Vi godkjenner kun klinikker med dokumentert minimum 4.0 stjerner på Google eller Trustpilot og minst 50 anmeldelser.' },
  { q: 'Dekker dere hele Norge?', a: 'Vi har partnerklinikker i Oslo, Bærum, Drammen, Lillestrøm, Asker og Sandvika. Vi utvider kontinuerlig til nye byer.' },
];

const JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    name: 'Specialists.no',
    url: 'https://specialists.no',
    description: 'Plattform som kobler pasienter med Norges beste private klinikker.',
    areaServed: 'NO',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

export default function Home() {
  const [clinics, setClinics] = useState([]);
  const [preselectCategory, setPreselectCategory] = useState('');
  const [preselectTreatment, setPreselectTreatment] = useState('');
  const [openFaq, setOpenFaq] = useState(-1);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get('/clinics/public').then((r) => setClinics(r.data.clinics || [])).catch(() => {});
  }, []);

  // Read ?treatment=<slug> and pre-fill the form, then scroll to it.
  useEffect(() => {
    const slug = searchParams.get('treatment');
    if (!slug) return;
    const config = treatmentBySlug(slug);
    if (!config) return;
    setPreselectCategory(config.category);
    setPreselectTreatment(config.treatment);
    trackCategory(config.category);
    setTimeout(() => {
      const form = document.getElementById('lead-form');
      if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  }, [searchParams]);

  const selectCategory = (cat) => {
    setPreselectCategory(cat.name);
    trackCategory(cat.name);
    const form = document.getElementById('lead-form');
    if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Layout>
      <Seo
        title="Specialists.no – Finn riktig spesialist raskt og trygt"
        description="Finn og bli kontaktet av Norges beste private klinikker. Gratis, uforpliktende og svar innen 24 timer."
        canonical="https://specialists.no/"
        jsonLd={JSONLD}
      />

      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden bg-[#0A1F44] text-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3B82F6]/20 rounded-full blur-[120px] -translate-y-1/4 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#3B82F6]/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-[1.25fr_1fr] gap-12 lg:gap-16 items-start">
            {/* Left */}
            <div className="anim-fade-up" style={{ animationDelay: '0.05s' }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#3B82F6]/40 bg-[#3B82F6]/10 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3B82F6] mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] anim-pulse" />
                Kun 4+ stjerner klinikker
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
                Finn din <span className="text-[#3B82F6]">riktige spesialist</span> — raskt og trygt
              </h1>

              <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-xl">
                Vi kobler deg med Norges best vurderte private klinikker. Fyll ut ett skjema — klinikken kontakter deg innen 24 timer. Gratis og uforpliktende.
              </p>

              <div className="mt-8 flex flex-wrap gap-x-7 gap-y-3 text-sm">
                {['Kun 4+ stjerner', 'Svar innen 24 timer', 'Gratis og uforpliktende'].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-white/80">
                    <Check size={16} className="text-[#3B82F6]" strokeWidth={2.5} />
                    {t}
                  </div>
                ))}
              </div>

              <div className="mt-10 flex items-center gap-5 pt-7 border-t border-white/10">
                <div className="flex items-center gap-1.5">
                  {[1,2,3,4,5].map((i) => (<Star key={i} size={15} fill="#3B82F6" className="text-[#3B82F6]" />))}
                  <span className="ml-1 text-white font-semibold text-sm">4.8</span>
                </div>
                <div className="h-4 w-px bg-white/15"></div>
                <div className="text-sm text-white/60">Over 2 000 pasienter har fått hjelp</div>
              </div>
            </div>

            {/* Right: form */}
            <div id="lead-form" className="anim-fade-up" style={{ animationDelay: '0.2s' }}>
              <LeadForm initialCategory={preselectCategory} initialTreatment={preselectTreatment} clinics={clinics} />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-3">SLIK FUNGERER DET</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0A1F44] leading-tight">Tre enkle steg til riktig spesialist</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { n: '01', t: 'Velg behandling', d: 'Fortell oss hvilken behandling du ønsker og velg klinikk — eller la oss foreslå en.' },
              { n: '02', t: 'Send forespørsel', d: 'Fyll ut et kort skjema. Under ett minutt, helt gratis og uforpliktende.' },
              { n: '03', t: 'Klinikken ringer deg', d: 'En spesialist kontakter deg innen 24 timer og avtaler konsultasjon.' },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="font-display text-7xl font-bold text-[#0A1F44]/8 leading-none absolute -top-6 -left-2 select-none -z-0">{s.n}</div>
                <div className="relative pt-10">
                  <h3 className="font-display text-2xl font-bold text-[#0A1F44] mb-2">{s.t}</h3>
                  <p className="text-[#64748B] leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section id="kategorier" className="bg-[#FAF8F5] py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-3">KATEGORIER</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0A1F44] leading-tight">Velg din behandling</h2>
            </div>
            <p className="text-[#64748B] max-w-md">Over 30 behandlinger på tvers av åtte kategorier. Alle utført av kvalifisert medisinsk personell.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map((c) => {
              const Icon = ICONS[c.icon] || Sparkles;
              const img = CATEGORY_IMAGES[c.slug];
              return (
                <Link
                  key={c.slug}
                  to={`/behandlinger/${c.slug}`}
                  onClick={() => trackCategory(c.name)}
                  data-testid={`category-${c.slug}`}
                  className="group text-left bg-white rounded-2xl overflow-hidden border border-[#0A1F44]/10 hover:border-[#0A1F44]/30 hover:-translate-y-1 transition-all duration-300 hover:shadow-xl"
                >
                  {img && (
                    <div className="h-32 w-full overflow-hidden bg-[#FAF8F5]">
                      <img
                        src={img}
                        alt={c.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="w-11 h-11 rounded-lg bg-[#0A1F44]/5 group-hover:bg-[#3B82F6]/10 flex items-center justify-center mb-4 transition-colors">
                      <Icon size={22} className="text-[#0A1F44] group-hover:text-[#3B82F6]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-lg font-bold text-[#0A1F44] mb-2">{c.name}</h3>
                    <p className="text-xs text-[#64748B] leading-relaxed mb-4 line-clamp-2">{c.description}</p>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#3B82F6] uppercase tracking-wider">
                      Se klinikker <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICE PREVIEW */}
      <section className="bg-white py-14 lg:py-20" aria-label="Prisguider">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#E8622A] mb-3">PRISGUIDER 2025</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0A1F44]">Hva koster behandlingen?</h2>
            <p className="text-[#64748B] mt-3 max-w-2xl mx-auto">
              Veiledende priser fra norske private klinikker. Sammenlign før du sender forespørsel.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-5">
            {[
              { name: 'Botox', price: '1 500 – 4 000 kr', slug: 'botox' },
              { name: 'Fillers', price: '2 000 – 5 000 kr', slug: 'fillers' },
              { name: 'Hårtransplantasjon', price: '25 000 – 60 000 kr', slug: 'hartransplantasjon' },
              { name: 'Tannbleking', price: '2 500 – 6 000 kr', slug: 'tannbleking' },
              { name: 'Fysioterapi', price: '800 – 1 500 kr per time', slug: 'fysioterapi' },
              { name: 'Øyenlaser', price: '15 000 – 25 000 kr', slug: 'oyenlaser' },
            ].map((p) => (
              <Link
                key={p.slug}
                to={`/priser/${p.slug}`}
                data-testid={`price-card-${p.slug}`}
                className="group bg-white rounded-xl p-5 lg:p-6 border-2 border-[#0A1F44]/8 hover:border-[#E8622A] hover:-translate-y-0.5 transition-all"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E8622A] mb-2">Veiledende pris</div>
                <div className="font-display text-xl lg:text-2xl font-bold text-[#0A1F44] mb-1.5">{p.name}</div>
                <div className="text-[#0A1F44] font-semibold text-base lg:text-lg">{p.price}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-xs lg:text-sm text-[#0A1F44] font-semibold group-hover:text-[#E8622A]">
                  Se prisguide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          <p className="text-xs text-[#64748B] text-center mt-6 italic">
            Prisene er veiledende og kan variere mellom klinikker og behandlere.
          </p>
          <div className="text-center mt-4">
            <Link
              to="/priser"
              data-testid="all-prices-link"
              className="inline-flex items-center gap-1.5 text-[#0A1F44] hover:text-[#E8622A] font-semibold text-sm border-b border-[#0A1F44]/20 hover:border-[#E8622A] pb-0.5 transition-colors"
            >
              Se alle prisguider <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PARTNER CLINICS */}
      <section className="bg-[#0A1F44] text-white py-14 lg:py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl -translate-y-1/4" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[11px] uppercase tracking-[0.18em] font-semibold text-white/80 mb-4">
                <Shield size={12} /> Kun godkjente klinikker
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">Våre partnerklinikker</h2>
              <p className="text-white/65 mt-3 max-w-xl">Vi samarbeider kun med klinikker som har 4+ stjerner</p>
            </div>
            <Link to="/klinikker" className="text-[#3B82F6] hover:text-white font-semibold inline-flex items-center gap-2">Se alle klinikker <ArrowRight size={16} /></Link>
          </div>

          <div className="overflow-x-auto scrollbar-thin -mx-4 px-4">
            <div className="flex gap-4 pb-4 min-w-max">
              {clinics.slice(0, 12).map((c) => (
                <Link
                  key={c.id}
                  to={`/klinikker/${c.id}`}
                  onClick={() => trackClinic(c.name, c.categories[0])}
                  data-testid={`clinic-card-${c.id}`}
                  className="w-72 flex-shrink-0 bg-white text-[#0A1F44] rounded-2xl p-6 hover:-translate-y-1 transition-transform"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-1">
                      <Star size={14} fill="#3B82F6" className="text-[#3B82F6]" />
                      <span className="font-semibold">{c.rating}</span>
                      <span className="text-xs text-[#64748B]">({c.reviews})</span>
                    </div>
                    {c.featured && <span className="text-[10px] uppercase tracking-wider font-bold text-[#3B82F6]">Partner</span>}
                  </div>
                  <h3 className="font-display text-lg font-bold leading-tight mb-1">{c.name}</h3>
                  <p className="text-sm text-[#64748B] mb-4">{c.location}</p>
                  <div className="flex items-center gap-2 text-xs">
                    <Clock size={12} className="text-[#3B82F6]" />
                    <span className="text-[#0A1F44] font-medium">{c.waitTime}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {c.categories.slice(0, 2).map((cat) => (
                      <span key={cat} className="text-[10px] uppercase tracking-wider bg-[#FAF8F5] text-[#0A1F44] px-2 py-1 rounded-full">{cat}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden border border-[#0A1F44]/10 shadow-[0_20px_60px_-25px_rgba(10,31,68,0.2)] order-last lg:order-first">
              <img
                src="/images/treatments/premium-clinic.png"
                alt="Eksklusivt skandinavisk klinikkmiljø"
                loading="lazy"
                className="w-full h-72 sm:h-96 object-cover"
              />
            </div>
            <div className="grid gap-8 lg:gap-10">
              {[
                { icon: Star, t: '4+ Stjerner', d: 'Kun klinikker med dokumentert høy rating på Google eller Trustpilot.' },
                { icon: Clock, t: 'Svar innen 24 timer', d: 'Klinikken forplikter seg til rask oppfølging via telefon eller e-post.' },
                { icon: Shield, t: 'GDPR-trygt', d: 'Kun delt med klinikken du selv valgte. Slettes etter 12 måneder.' },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t} className="flex gap-5">
                  <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={22} className="text-[#3B82F6]" strokeWidth={1.8} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-[#0A1F44] mb-1">{t}</h3>
                    <p className="text-[#64748B] leading-relaxed text-[15px]">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="bg-[#FAF8F5] py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-3">ARTIKLER</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0A1F44]">Helseartikler og behandlingsguider</h2>
            </div>
            <Link to="/artikler" className="text-[#0A1F44] font-semibold inline-flex items-center gap-2 hover:text-[#3B82F6]">Se alle artikler <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {ARTICLES.slice(0, 3).map((a) => (
              <Link key={a.slug} to={`/artikler/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group bg-white rounded-2xl p-6 border border-[#0A1F44]/10 hover:border-[#0A1F44]/30 hover:-translate-y-1 hover:shadow-lg transition-all">
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#3B82F6] mb-3">{a.category} · {a.readingTime} min</div>
                <h3 className="font-display text-xl font-bold text-[#0A1F44] mb-3 leading-tight group-hover:text-[#3B82F6] transition-colors">{a.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed line-clamp-3">{a.excerpt}</p>
                <div className="flex items-center gap-1 mt-4 text-sm text-[#3B82F6] font-semibold">Les mer <ArrowRight size={14} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-3">OFTE STILTE SPØRSMÅL</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0A1F44]">Vi svarer på det viktigste</h2>
          </div>
          <div className="space-y-2">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-[#FAF8F5] rounded-xl overflow-hidden">
                <button
                  data-testid={`faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left"
                >
                  <span className="font-display text-lg font-semibold text-[#0A1F44]">{f.q}</span>
                  <ChevronDown size={18} className={`text-[#0A1F44] flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div className={`grid transition-all ${openFaq === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                  <div className="overflow-hidden">
                    <div className="px-6 pb-6 text-[#64748B] leading-relaxed">{f.a}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
